/**
 * The tiny per-channel status pills used on history rows, the message detail
 * table, and a member's own communications list.
 */
import type { DeliveryStatus, MessageSummary } from '@gwc/shared';
import { Badge } from './ui';

export const STATUS_TONE: Record<string, 'neutral' | 'gold' | 'success' | 'warning' | 'danger'> = {
  pending: 'neutral',
  sending: 'neutral',
  sent: 'gold',
  delivered: 'success',
  bounced: 'danger',
  failed: 'danger',
  skipped: 'neutral',
};

export const STATUS_LABEL: Record<string, string> = {
  pending: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  bounced: 'Bounced',
  failed: 'Failed',
  skipped: 'Skipped',
};

export function StatusBadge({ status }: { status: DeliveryStatus | string }) {
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{STATUS_LABEL[status] ?? status}</Badge>;
}

export const MESSAGE_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  queued: 'Sending',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/** "12 email · 9 delivered · 1 bounced" style rollup from a summary's delivery map. */
export function rollupSummary(m: Pick<MessageSummary, 'delivery' | 'recipientCount'>) {
  const email = m.delivery.email ?? {};
  const push = m.delivery.push ?? {};
  const sum = (o: Partial<Record<string, number>>, keys: string[]) =>
    keys.reduce((n, k) => n + (o[k] ?? 0), 0);
  return {
    recipients: m.recipientCount,
    emailSent: sum(email, ['sent', 'delivered']),
    emailDelivered: email.delivered ?? 0,
    emailFailed: sum(email, ['failed', 'bounced']),
    emailPending: sum(email, ['pending', 'sending']),
    pushSent: sum(push, ['sent', 'delivered']),
    pushFailed: push.failed ?? 0,
    failed: sum(email, ['failed', 'bounced']) + (push.failed ?? 0),
  };
}

export function RollupLine({ m }: { m: Pick<MessageSummary, 'delivery' | 'recipientCount' | 'channels'> }) {
  const r = rollupSummary(m);
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-cream-300/70">
      <span>
        {r.recipients} recipient{r.recipients === 1 ? '' : 's'}
      </span>
      {m.channels.includes('email') ? (
        <span>
          {r.emailSent} email
          {r.emailDelivered > 0 ? <span className="text-success"> · {r.emailDelivered} delivered</span> : null}
        </span>
      ) : null}
      {m.channels.includes('push') && r.pushSent > 0 ? <span>{r.pushSent} push</span> : null}
      {r.emailPending > 0 ? <span className="text-gold-300">{r.emailPending} queued</span> : null}
      {r.failed > 0 ? <span className="text-danger">{r.failed} failed</span> : null}
    </span>
  );
}
